<template>
  <div class="details-grid" :class="{ 'details-grid-compact': compact }">
    <template v-if="eventsLoading">
      <section class="detail-card" aria-hidden="true">
        <p class="detail-label">Timing</p>
        <div class="detail-stat-row">
          <div>
            <span class="detail-stat-label">Timestamp</span>
            <span class="skeleton-block skeleton-stat"></span>
          </div>
          <div>
            <span class="detail-stat-label">Duration</span>
            <span class="skeleton-block skeleton-stat"></span>
          </div>
        </div>
      </section>

      <section class="detail-card detail-card-wide" aria-hidden="true">
        <p class="detail-label">Feedback</p>
        <div class="feedback-copy">
          <p class="skeleton-block skeleton-line skeleton-line-medium"></p>
          <p class="skeleton-block skeleton-line"></p>
        </div>
        <div class="feedback-actions">
          <span class="skeleton-block skeleton-button"></span>
          <span class="skeleton-block skeleton-button"></span>
        </div>
      </section>
    </template>

    <section v-else-if="activeEvent.id" class="detail-card">
      <p class="detail-label">Timing</p>
      <div class="detail-stat-row">
        <div>
          <span class="detail-stat-label">Timestamp</span>
          <span class="detail-stat-value">{{ formatDuration(activeEvent.timestamp) }}</span>
        </div>
        <div>
          <span class="detail-stat-label">Duration</span>
          <span class="detail-stat-value">{{ activeEvent.duration }}</span>
        </div>
      </div>
    </section>

    <section v-if="!eventsLoading && activeEvent.id" class="detail-card">
      <ceremony-section-export
        :active-event="activeEvent"
        :export-state="exportState"
        :format-duration="formatDuration"
        @export-event="$emit('export-event', $event)"
        @download-export="$emit('download-export', $event)"
      />
    </section>

    <section v-if="!eventsLoading && activeEvent.id && !isHighlightEvent" class="detail-card detail-card-wide">
      <p class="detail-label">Feedback</p>
        <template v-if="!eventFeedback">
          <div class="feedback-copy">
            <p class="feedback-title">Was this what you were expecting?</p>
            <p class="feedback-text">Your response helps us understand whether this result matched the search intent.</p>
          </div>
          <div class="feedback-actions">
            <button type="button" class="feedback-button" :disabled="feedbackSubmitting" @click="submitFeedback('yes')">Yes</button>
            <button type="button" class="feedback-button feedback-button-secondary" :disabled="feedbackSubmitting" @click="submitFeedback('no')">No</button>
          </div>
          <p v-if="feedbackError" class="feedback-error" role="alert">{{ feedbackError }}</p>
        </template>
        <template v-else>
          <p class="feedback-saved">
            Feedback recorded: {{ recordedFeedbackResponse }}
          </p>
        </template>
    </section>
  </div>
</template>

<script>
import CeremonySectionExport from './CeremonySectionExport.vue'

export default {
  name: 'CeremonyEventDetails',
  components: {
    CeremonySectionExport,
  },
  props: {
    activeEvent: {
      type: Object,
      default: () => ({}),
    },
    eventsLoading: {
      type: Boolean,
      default: false,
    },
    formatDuration: {
      type: Function,
      required: true,
    },
    eventFeedback: {
      type: [String, Object],
      default: '',
    },
    exportState: {
      type: Object,
      default: () => ({}),
    },
    feedbackSubmitting: {
      type: Boolean,
      default: false,
    },
    feedbackError: {
      type: String,
      default: '',
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isHighlightEvent() {
      return this.activeEvent?.type === 'highlight'
    },
    recordedFeedbackResponse() {
      if (!this.eventFeedback) return ''
      if (typeof this.eventFeedback === 'string') return this.eventFeedback
      return this.eventFeedback.response || ''
    },
  },
  methods: {
    submitFeedback(response) {
      if (!this.activeEvent?.id || this.eventFeedback) return
      this.$emit('submit-feedback', {
        eventId: this.activeEvent.id,
        response,
      })
    },
  },
}
</script>

<style scoped>
.details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 14px;
}

.details-grid-compact {
  max-width: 880px;
  margin-inline: auto;
}

.detail-card {
  border: 1px solid #d6dbe5;
  border-radius: .25rem;
  padding: 18px;
  background: #fcfdff;
}

.detail-card-wide {
  grid-column: 1 / -1;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 8px;
}

.detail-heading {
  font-size: 1.1rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 10px;
}

.detail-copy {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #475569;
}

.feedback-panel {
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: .25rem;
  background: rgba(241, 245, 249, 0.72);
  border: 1px solid rgba(203, 213, 225, 0.82);
}

.feedback-copy {
  margin-bottom: 12px;
}

.feedback-title {
  margin: 0 0 4px;
  font-size: 0.92rem;
  font-weight: 650;
  color: #0f172a;
}

.feedback-text {
  margin: 0;
  font-size: 0.84rem;
  line-height: 1.45;
  color: #526076;
}

.feedback-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.feedback-button {
  min-width: 86px;
  height: 38px;
  padding: 0 14px;
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: .25rem;
  background: #0f172a;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
}

.feedback-button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.feedback-button-secondary {
  border-radius: .25rem;
  background: #fff;
  color: #0f172a;
}

.feedback-saved {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 0.86rem;
  color: #334155;
}

.feedback-error {
  margin: 10px 0 0;
  color: #b42318;
  font-size: 0.84rem;
}

.feedback-saved-pill {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  background: #0f172a;
  color: #fff;
  font-weight: 700;
  text-transform: capitalize;
}

.detail-stat-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.detail-stat-label,
.detail-stat-value {
  display: block;
}

.detail-stat-label {
  font-size: 0.75rem;
  color: #64748b;
  margin-bottom: 4px;
}

.detail-stat-value {
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
}

.skeleton-block {
  display: block;
  border-radius: 999px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f8fafc 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.3s ease-in-out infinite;
}

.skeleton-stat {
  width: 70%;
  height: 18px;
}

.skeleton-line {
  width: 100%;
  height: 13px;
  border-radius: 8px;
}

.skeleton-line-medium {
  width: 58%;
  margin-bottom: 10px;
}

.skeleton-button {
  width: 86px;
  height: 38px;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 1023px) {
  .details-grid {
    grid-template-columns: 1fr;
  }

  .detail-card-wide {
    grid-column: auto;
  }
}
</style>
